import * as React from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import type { ClientsWithUsers } from "@/client-admin";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface SelectClientNameComboBoxProps {
  value: number | null;
  onChange: (value: number | null) => void;
  data: ClientsWithUsers | undefined;
  placeholder?: string;
  isLoading?: boolean;
}

export function SelectClientNameComboBox({
  value,
  onChange,
  data,
  placeholder = "Select client name...",
  isLoading = false,
}: SelectClientNameComboBoxProps) {
  const [open, setOpen] = React.useState(false);

  const clientNames = React.useMemo(() => {
    if (!data?.data) return [];
    return data.data.map((client) => ({
      value: client.id,
      label: client.client_name,
    }));
  }, [data]);

  const selectedLabel = React.useMemo(() => {
    if (!value) return placeholder;
    return clientNames.find((name) => name.value === value)?.label || placeholder;
  }, [value, clientNames, placeholder]);

  if (isLoading)
    return (
      <Button variant="outline" disabled>
        {placeholder}
      </Button>
    );
  if (!data) return <Button variant="outline">{placeholder}</Button>;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild className="w-full sm:w-[250px]">
        <Button variant="outline" role="combobox" aria-expanded={open} className={`justify-between`}>
          {selectedLabel}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent>
        <Command>
          {/* Changed placeholder */}
          <CommandInput placeholder="Search client name..." />
          <CommandList>
            {/* Changed message */}
            <CommandEmpty>No client name found.</CommandEmpty>
            <CommandGroup>
              {value !== null && (
                <CommandItem
                  key="clear-selection"
                  value="clear-selection"
                  onSelect={() => {
                    onChange(null);
                    setOpen(false);
                  }}
                  className="text-muted-foreground"
                >
                  <X className="mr-2 h-4 w-4" />
                  Clear selection
                </CommandItem>
              )}
              {clientNames.map((clientName) => (
                <CommandItem
                  key={clientName.value}
                  value={clientName.label}
                  onSelect={(currentValueLabel) => {
                    const selectedClient = clientNames.find(
                      (c) => c.label.toLowerCase() === currentValueLabel.toLowerCase(),
                    );
                    const selectedValue = selectedClient ? selectedClient.value : null;
                    onChange(selectedValue === value ? null : selectedValue);
                    setOpen(false);
                  }}
                >
                  <Check className={cn("mr-2 h-4 w-4", value === clientName.value ? "opacity-100" : "opacity-0")} />
                  {clientName.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
